import {
  ALERT_SECTION_LABELS,
  Alert,
  AlertSection,
  AlertType,
  AlertsCommentary,
  nonDismissibleAlertTypes,
} from '@aloft/toolkit-shared';
import { Button, Flex, Input, Typography, theme, useThemeColor } from '@aloft/toolkit-sui';

import { debounce, isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useZodForm } from '../../../hooks/useZodForm.ts';
import { cancelDismissAlert, dismissAlert } from '../../../redux/features/alertsSlice.ts';
import { setCommentaryV2 } from '../../../redux/features/commentarySlice.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsInspectifyOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { useAlerts } from './hooks/useAlerts.tsx';

const AlertItem = ({ alert, index }: { alert: Alert; index: number }) => {
  const {
    type,
    status,
    message,
    compType,
    fixingInfo: { tips, pairedCostRouteItem },
  } = alert;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);

  const { control } = useFormContext<AlertsCommentary>();

  const isDismissed = status === 'dismissed';
  const isNonDismissable = nonDismissibleAlertTypes.includes(type);
  const canDismiss = !isNonDismissable;

  return (
    <Flex key={type} flexDirection="column" gap="0.5rem" alignItems="flex-end">
      <Flex justifyContent="space-between" alignItems="flex-start" gap="1rem" width="100%">
        <Flex flex={1}>
          <Typography variant="body1">
            {index + 1}. {message}
          </Typography>
        </Flex>
        {compType && (
          <Flex
            padding="4px 8px"
            borderRadius="4px"
            alignItems="center"
            justifyContent="center"
            __dangerDoNotUseSx={{
              backgroundColor: theme.colors[compType],
              color: theme.colors[`${compType}Text`],
            }}
          >
            <Typography
              variant="body2"
              __dangerDoNotUseSx={{ fontSize: '10px', fontWeight: 600, lineHeight: 'normal' }}
            >
              {compType.toUpperCase()}
            </Typography>
          </Flex>
        )}
      </Flex>

      {!isDismissed && (
        <Flex width="100%" paddingLeft="20px">
          <Typography variant="body2" color="surfaceAccent4">
            {tips}
          </Typography>
        </Flex>
      )}

      {isDismissed && (
        <Controller
          name={type}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Flex width="100%" flexDirection="column" gap="0.25rem" paddingLeft="20px">
              <Input
                type="multiline"
                resize="vertical"
                __dangerDoNotUseSx={
                  error ? { borderColor: useThemeColor('alertError') } : undefined
                }
                value={value}
                onClear={() => onChange('')}
                onChange={onChange}
              />
              <Flex justifyContent="space-between" alignItems="center">
                {error ? (
                  <Typography variant="body2" color="alertError">
                    Required
                  </Typography>
                ) : (
                  <Flex />
                )}
                {canDismiss && (
                  <Button
                    variant="inline"
                    size="sm"
                    text="Cancel Dismiss"
                    onClick={() => dispatch(cancelDismissAlert(type))}
                  />
                )}
              </Flex>
            </Flex>
          )}
        />
      )}

      {!isDismissed && (
        <Flex width="100%" justifyContent="flex-end" gap="0.5rem">
          {isInspectifyOrder && (
            <Button
              size="sm"
              text="Fix Now"
              onClick={() => navigate(`paired-cost/${pairedCostRouteItem}`)}
            />
          )}
          {canDismiss && (
            <Button
              variant="secondary"
              size="sm"
              text="Dismiss"
              onClick={() => dispatch(dismissAlert(type))}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export const AlertsContent = ({ alerts }: { alerts: ReturnType<typeof useAlerts> }) => {
  const dispatch = useAppDispatch();

  const commentaryV2 = useAppSelector(selectCommentaryV2);

  const dismissedAlertTypes = useMemo(
    () =>
      Object.entries(alerts)
        .flatMap(([_section, sectionAlerts]) => sectionAlerts)
        .filter(({ status }) => status === 'dismissed')
        .map(({ type }) => type),
    [alerts],
  );

  const schDismissedAlertsCommentary = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          dismissedAlertTypes.map((type) => [type, z.string().min(1, 'This field is required')]),
        ),
      ),
    [dismissedAlertTypes],
  );

  const zodForm = useZodForm({
    schema: schDismissedAlertsCommentary,
    defaultValues: useMemo(() => commentaryV2.alerts ?? {}, []),
    mode: 'all',
  });

  const { watch } = zodForm;

  const debouncedSetCommentary = useMemo(
    () =>
      debounce((newAlertsCommentary: AlertsCommentary | undefined) => {
        if (isEqual(newAlertsCommentary, commentaryV2.alerts)) return;

        dispatch(
          setCommentaryV2({
            ...commentaryV2,
            alerts: newAlertsCommentary,
          }),
        );
      }, 300),
    [dispatch, commentaryV2],
  );

  useEffect(() => {
    // For some reason the form doesn't validate on mount, so we trigger it manually
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    zodForm.trigger();
  }, [dismissedAlertTypes, zodForm]);

  useEffect(() => {
    const subscription = watch((formValues) => {
      const cleanedFormData = Object.entries(formValues)
        .map(([k, v]) => [k, v === '' ? undefined : v?.trim()])
        .filter(([_k, v]) => v !== undefined) as [AlertType, string][];

      debouncedSetCommentary(
        cleanedFormData.length ? Object.fromEntries(cleanedFormData) : undefined,
      );
    });

    return () => subscription.unsubscribe();
  }, [dispatch, watch, debouncedSetCommentary]);

  if (Object.values(alerts).flat().length === 0) {
    return <Typography>🎉 Congrats, no alerts remain!</Typography>;
  }

  return (
    <FormProvider {...zodForm}>
      {Object.entries(alerts)
        .filter(([_section, sectionAlerts]) => sectionAlerts.length > 0)
        .map(([section, sectionAlerts]) => (
          <Flex key={section} flexDirection="column" gap="1rem">
            <Typography variant="h2">{ALERT_SECTION_LABELS[section as AlertSection]}</Typography>
            {sectionAlerts.map((alert, index) => (
              <AlertItem key={alert.type} alert={alert} index={index} />
            ))}
          </Flex>
        ))}
    </FormProvider>
  );
};
